import React, { useEffect } from 'react';
import { Box, Container, Grid, Typography, Button, Card, CardContent, Avatar, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import { Helmet } from 'react-helmet-async';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';


export default function PRAgencySection() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);

  const features = [
    {
      title: "Media Relations",
      description: "Build strong relationships with journalists and secure valuable media coverage.",
      icon: "📰",
      likes: "2.8K",
      comments: "198"
    },
    {
      title: "Crisis Management",
      description: "Handle reputation challenges effectively with strategic communication plans.",
      icon: "🛡️",
      likes: "2.6K",
      comments: "187"
    },
    {
      title: "Brand Storytelling",
      description: "Craft compelling narratives that resonate with your target audience.",
      icon: "📖",
      likes: "3.1K",
      comments: "234"
    },
    {
      title: "Event Management",
      description: "Organize and promote events that enhance your brand presence.",
      icon: "🎪",
      likes: "2.9K",
      comments: "212"
    }
  ];

  const benefits = [
    "Enhanced brand reputation",
    "Increased media coverage",
    "Better crisis handling",
    "Stronger brand presence",
    "Improved stakeholder relations",
    "Strategic communication"
  ];
<Helmet>
  <title>PR Agency Services in Visakhapatnam | SLGT Digital</title>
  <meta
    name="description"
    content="Enhance your brand reputation with SLGT Digital’s professional PR services in Visakhapatnam. Media coverage, crisis management, storytelling & more."
  />
  <meta
    name="keywords"
    content="PR agency Visakhapatnam, Public Relations Vizag, Crisis Management, Media Coverage, Brand Storytelling, SLGT Digital PR"
  />
  <link rel="canonical" href="https://www.slgt.in/services/pr-agency" />

  {/* Open Graph Meta Tags */}
  <meta property="og:title" content="PR Agency Services in Visakhapatnam | SLGT Digital" />
  <meta property="og:description" content="SLGT Digital helps you build and manage your brand's reputation through expert PR services." />
  <meta property="og:image" content="https://www.slgt.in/assets/services/pr-1.jpg" />
  <meta property="og:url" content="https://www.slgt.in/services/pr-agency" />
  <meta property="og:type" content="website" />

  {/* Twitter Card Meta Tags */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="PR Agency Services in Visakhapatnam | SLGT Digital" />
  <meta name="twitter:description" content="Build a strong brand reputation with PR services by SLGT Digital in Vizag." />
  <meta name="twitter:image" content="https://www.slgt.in/assets/services/pr-1.jpg" />

  {/* JSON-LD Structured Schema */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Public Relations",
      "provider": {
        "@type": "Organization",
        "name": "SLGT Digital",
        "url": "https://www.slgt.in",
        "logo": "https://www.slgt.in/logo.png",
        "sameAs": [
          "https:///slgtdigital",
          "https://www.facebook.com/slgtdigital"
        ]
      },
      "areaServed": {
        "@type": "Place",
        "name": "Visakhapatnam"
      },
      "url": "https://www.slgt.in/services/pr-agency",
      "description": "SLGT Digital offers expert PR services including media relations, crisis management, storytelling, and event promotion to help brands grow and protect their reputation.",
      "image": "https://www.slgt.in/assets/services/pr-1.jpg"
    })}
  </script>
</Helmet>

  return (
    <>
<Box
  sx={{
    position: 'fixed',
    top: { xs: 50, sm: 70 },
    left: { xs: 12, sm: 24 },
    zIndex: 1200,
    bgcolor: 'white',
    borderRadius: '50%',
    boxShadow:5,
    width: { xs: 40, sm: 48 },
    height: { xs: 40, sm: 48 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <IconButton
    onClick={() => navigate(-1)}
    size="medium"
    sx={{
      color: '#1877f2',
      p: 0.5
    }}
  >
    <ArrowBackIosNewIcon fontSize="small" />
  </IconButton>
</Box>
    <Box sx={{ 
      pt: { xs: 8, sm: 10 },
      pb: { xs: 6, sm: 8 },
      minHeight: '100vh',
      bgcolor: '#f0f2f5'
    }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: '#ffffff',
          color: '#1c1e21',
          py: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 },
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    mr: 2,
                    bgcolor: '#1877f2'
                  }}
                >
                  PR
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      color: '#1c1e21'
                    }}
                  >
                    Public Relations Agency
                  </Typography>
                  <Typography
                    sx={{
                      color: '#65676b',
                      fontSize: '0.9rem',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                    }}
                  >
                    Professional PR Services
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  mb: { xs: 2, md: 3 },
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  color: '#1c1e21',
                  lineHeight: 1.2
                }}
              >
                Build Your Brand's Reputation
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  mb: { xs: 3, md: 4 },
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  color: '#65676b',
                  lineHeight: 1.6
                }}
              >
                Strengthen your brand's presence and manage your reputation with our expert PR services.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/requestquotes')}
                sx={{
                  bgcolor: '#1877f2',
                  color: 'white',
                  '&:hover': { bgcolor: '#166fe5' },
                  px: { xs: 4, sm: 6 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  borderRadius: '6px',
                  textTransform: 'none'
                }}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <img
                src="/assets/services/pr-1.jpg"
                alt="PR Agency Services Illustration"
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                  borderRadius: '8px'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: { xs: 6, md: 8 } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            textAlign: 'center',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            color: '#1c1e21',
            lineHeight: 1.2
          }}
        >
          Our PR Services
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  bgcolor: '#ffffff'
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Post Header */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e4e6eb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: '#1877f2' }}>
                        {feature.icon}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            color: '#1c1e21'
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            color: '#65676b',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                          }}
                        >
                          Professional PR Service
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Post Content */}
                  <Box sx={{ p: 2 }}>
                    <Typography
                      sx={{
                        color: '#1c1e21',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>

                  {/* Post Actions */}
                  <Box sx={{ 
                    p: 1, 
                    borderTop: '1px solid #e4e6eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton size="small" sx={{ color: '#1877f2' }}>
                        <FavoriteIcon />
                      </IconButton>
                      <Typography sx={{ color: '#65676b', fontSize: '0.9rem' }}>
                        {feature.likes}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton size="small" sx={{ color: '#65676b' }}>
                        <CommentIcon />
                      </IconButton>
                      <Typography sx={{ color: '#65676b', fontSize: '0.9rem' }}>
                        {feature.comments}
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: '#65676b' }}>
                      <ShareIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              textAlign: 'center',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              color: '#1c1e21',
              lineHeight: 1.2
            }}
          >
            Benefits of Professional PR
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    bgcolor: '#ffffff'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 2,
                          bgcolor: '#1877f2'
                        }}
                      >
                        ✓
                      </Avatar>
                      <Typography
                        sx={{
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontSize: '1rem',
                          color: '#1c1e21',
                          fontWeight: 500
                        }}
                      >
                        {benefit}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Card 
          sx={{ 
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            bgcolor: '#ffffff',
            p: { xs: 3, md: 4 }
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 3,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              color: '#1c1e21',
              lineHeight: 1.2
            }}
          >
            Ready to Build Your Brand's Reputation?
          </Typography>
          <Typography
            sx={{
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              maxWidth: '800px',
              mx: 'auto',
              color: '#65676b',
              lineHeight: 1.6,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
            }}
          >
            Let's create a powerful PR strategy that helps you build and maintain a strong brand reputation.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/requestquotes')}
            sx={{
              bgcolor: '#1877f2',
              color: 'white',
              '&:hover': { bgcolor: '#166fe5' },
              px: { xs: 4, sm: 6 },
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600,
              borderRadius: '6px',
              textTransform: 'none'
            }}
          >
            Request a Quote
          </Button>
        </Card>
      </Container>
    </Box>
  );
  </>
  );
}
